import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import cx from '@/utils/cx';
import useStyle from './style';

const InputMask = ({
  mask,
  dynamicChars,
  inputProps,
  value,
  onChange,
  className,
  ...rest
}) => {
  const classes = useStyle();
  const hiddenInput = useRef();
  const calcedDynamicChars = Object.assign(InputMask.defaultProps.dynamicChars, dynamicChars);

  useEffect(() => {
    if (hiddenInput) {
      hiddenInput.current.value = value.join('');
    }
  }, [value]);

  const localValue = (() => {
    const allDynamicChars = Object.keys(calcedDynamicChars).filter((x) => typeof dynamicChars[x] === 'function');
    const temp = [];
    let j = 0;
    mask.split('').forEach((char) => {
      if (allDynamicChars.includes(char)) {
        temp.push({
          type: 'dynamic',
          char,
          value: value[j] || '',
        });
        j += 1;
      } else if (typeof temp[temp.length - 1] !== 'undefined' && temp[temp.length - 1].type === 'static') {
        temp[temp.length - 1].value += char;
      } else {
        temp.push({
          type: 'static',
          char,
          value: char,
        });
      }
    });
    return temp;
  })();


  const callOnChange = (newLocalValue) => {
    onChange(
      newLocalValue.filter((x) => x.type === 'dynamic').map((x) => x.value),
    );
  };

  const checkDynamicPartValidation = (char, val) => {
    if (typeof calcedDynamicChars[char] === 'function') {
      return !!calcedDynamicChars[char](val);
    }
    return val.length === 1;
  };

  const formatDynamicPart = (char, val) => {
    if (typeof calcedDynamicChars[char] === 'function') {
      const temp = calcedDynamicChars[char](val);
      if (typeof temp === 'string') {
        return temp;
      }
    }
    return val;
  };

  const getActivePart = () => {
    for (let i = 0; i < localValue.length; i += 1) {
      if (localValue[i].type === 'dynamic' && !checkDynamicPartValidation(localValue[i].char, localValue[i].value)) {
        return i;
      }
      // use last dynamic
      if (i === localValue.length - 1) {
        for (let j = localValue.length - 1; j >= 0; j -= 1) {
          if (localValue[j].type === 'dynamic') {
            return j;
          }
        }
      }
    }
    return -1;
  };

  const putLocalValue = (val) => {
    const activePart = getActivePart();
    if (activePart !== -1) {
      const cloned = JSON.parse(JSON.stringify(localValue));
      const part = cloned[activePart];
      const tempValue = val.length > 1 ? val : part.value + val;
      if (checkDynamicPartValidation(part.char, tempValue)) {
        part.value = tempValue;
        callOnChange(cloned);
      }
    }
  };

  const popLocalValue = () => {
    const cloned = JSON.parse(JSON.stringify(localValue));
    for (let i = cloned.length - 1; i > -1; i -= 1) {
      if (cloned[i].type === 'dynamic' && cloned[i].value !== '') {
        cloned[i].value = cloned[i].value.substr(0, cloned[i].value.length - 1);
        break;
      }
    }
    callOnChange(cloned);
  };

  const handleChange = (event) => {
    const inputValue = event.target.value;
    const prevValueLength = value.join('').length;
    if (prevValueLength > inputValue.length) {
      popLocalValue();
      return;
    }

    if (inputValue.length - prevValueLength > 1) {
      putLocalValue(inputValue);
      return;
    }

    putLocalValue(inputValue.substr(-1));
  };

  const setFocus = () => {
    if (hiddenInput) {
      hiddenInput.current.focus();
    }
  };

  return (
    // eslint-disable-next-line max-len
    /* eslint-disable jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */
    <div
      className={cx(classes.inputMask, className)}
      onClick={setFocus}
    >
      <input className="hidden-input" onChange={handleChange} dir="ltr" tabIndex="0" ref={hiddenInput} {...inputProps} {...rest} />
      <div className="shown-input">
        {
          localValue.map((part, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <span key={index} className={cx('part', part.type)}>
              {formatDynamicPart(part.char, part.value)}
              {getActivePart() === index && (<span className="caret">|</span>)}
            </span>
          ))
        }
      </div>
    </div>
  );
};


InputMask.propTypes = {
  /**
   * To define your mask template, write your template here.
   * You can use your defined dynamicChars here.
  */
  mask: PropTypes.string,
  /**
   * To define your custom dynamic chars,
   * use one length string as Key and pass validator/formatter function as Value.
   * Note that your Object will assign to pre-defined Object.
   * Pre defined dynamicChars listed bellow: <br>
   * **Y** is currency.
   * **C** is character.
   * **C** is string.
   * **d** is one-length number.
  */
  dynamicChars: PropTypes.objectOf(PropTypes.func),
  /**
   * This one is array of dynamic part of mask.
  */
  value: PropTypes.array,
  onChange: PropTypes.func,
  /**
   * This one is object of custom input props.
  */
  inputProps: PropTypes.object,
};

InputMask.defaultProps = {
  mask: 'S',
  dynamicChars: {
    Y: (k) => (/^\d+$/.test(k.toString()) ? k.toString().replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,').replace(/^0+/g, '') : false),
    C: (k) => (k.toString().length === 1 ? k : false),
    S: (k) => k.toString(),
    D: (k) => (k.toString().length === 1 && /^\d+$/.test(k.toString()) ? k : false),
  },
  value: [],
  onChange: () => { },
  inputProps: {},
};

export default InputMask;
